import { Box, HStack, Image, Stack, Text } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { BoxProps } from '@chakra-ui/react';

import { cartDetailsDrawerOpenAtom } from '$business/atoms/cart-details-drawer';
import { dbg } from '$src/utils/devlog';
import { ExitIntentContext } from '$src/context/ExitIntentContext';
import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { useCart, useShop } from '$src/hooks/queries';
import { useROFormMessageAtom } from '$src/hooks/state';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';

const HeaderChevron = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',
      flexShrink: 0,
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Header = (props: BoxProps) => {
  const { t } = useTranslation('common');
  const { data: shop } = useShop();
  const { data: cart } = useCart();
  const [heroDetailsOpen, setHeroDetailsOpen] = useAtom(cartDetailsDrawerOpenAtom);
  const brandingPreview = useROBrandingPreviewAtom();
  const exitIntentContext = useContext(ExitIntentContext);

  // REMOVE CONDITION AFTER MIGRATION -----------------------------------
  const urlLogoShop = useMemo(
    () =>
      shop?.logo?.includes('https://')
        ? shop?.logo
        : `https://${process.env.NEXT_PUBLIC_LOGOS_BUCKET}.s3.eu-west-3.amazonaws.com/${shop?.logo}`,
    [shop],
  );

  // --------------------------------------------------------------------
  const previewLogoUrl = brandingPreview?.logoUrl;
  const effectiveLogoUrl = previewLogoUrl ?? urlLogoShop;
  const hasLogo = previewLogoUrl ? true : !!shop?.logo;
  const [logoLoadFailed, setLogoLoadedFailed] = useState(false);
  const [logoSrcSet, setLogoSrcSet] = useState('');
  const { isMobile } = useIsDesktop();
  const message = useROFormMessageAtom();
  const logoBoxRef = useRef<HTMLDivElement>(null);

  const onToggleHeroDetails = useCallback(() => {
    const rect = logoBoxRef.current?.getBoundingClientRect();
    dbg('header-toggle', {
      open: !heroDetailsOpen,
      logoCenterX: rect ? Math.round(rect.left + rect.width / 2) : undefined,
      logoLeft: rect ? Math.round(rect.left) : undefined,
      logoWidth: rect ? Math.round(rect.width) : undefined,
    });
    setHeroDetailsOpen((v) => !v);
  }, [heroDetailsOpen, setHeroDetailsOpen]);

  useEffect(() => {
    if (!shop) return;
    if (shop.logoVariants?.length) {
      setLogoSrcSet(
        shop.logoVariants
          ?.map((variant) => `${shop.baseMediaUrl}${variant.fileKey} ${variant.name}`)
          .join(', ') ?? '',
      );
    }
  }, [shop]);

  // Background injection is now handled by ServerBackgroundImage component for better performance

  const executeExit = useCallback(() => {
    const target = message?.origin;
    const isSafeUrl = target && /^https?:\/\//i.test(target);
    window.location.replace(isSafeUrl ? target : '/');
  }, [message?.origin]);

  const onBack = useCallback(() => {
    // Try to show exit intent popup first
    if (exitIntentContext) {
      const wasShown = exitIntentContext.triggerExitIntent();
      if (wasShown) {
        // Store the exit action to execute after user selects a reason
        // Double arrow function: outer one for React setState (functional update pattern),
        // inner one is the actual callback to store
        exitIntentContext.setPendingExitCallback(() => () => executeExit());
        return;
      }
    }
    // If popup wasn't shown (already shown or order processing), proceed with exit
    executeExit();
  }, [exitIntentContext, executeExit]);

  const logoContent =
    !logoLoadFailed && hasLogo ? (
      <Image
        maxH="100%"
        h={isMobile ? '1.25rem' : '2.5rem'}
        maxW="100%"
        objectFit="contain"
        alt={t('aria.shop_logo')}
        textAlign="center"
        src={effectiveLogoUrl}
        srcSet={previewLogoUrl ? undefined : logoSrcSet}
        onError={() => setLogoLoadedFailed(true)}
      />
    ) : (
      <Text textAlign="center" color="var(--text, #1C1C1C)" noOfLines={1}>
        {shop?.name ?? ''}
      </Text>
    );

  return (
    <Box
      alignItems="center"
      justifyContent="space-between"
      {...props}
      mb="2px"
      px={4}
      py={2}
      w="100%"
      display="flex"
      position="relative"
    >
      {/* Flow row, left-aligned. Mobile: the back arrow (logo is centered
          absolutely below). Desktop: the logo sits here on the left in place of
          the arrow — no back arrow on desktop. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="start"
        w="full"
        h={!isMobile ? 'full' : undefined}
      >
        {isMobile ? (
          <Box display="flex" alignItems="center" justifyContent="start" ml="0">
            <ArrowBackIcon onBack={onBack} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="start" ml="6" maxW="50%">
            {logoContent}
          </Box>
        )}
      </Stack>

      {/* Mobile: logo absolutely pinned to the header's center (on desktop the
          logo renders left, in the flow row above, and there's no back arrow).
          Capped maxW reserves room for the cart toggle; pointerEvents=none so it
          never intercepts taps meant for the toggle. */}
      {isMobile && (
        <Box
          ref={logoBoxRef}
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          maxW="calc(100% - 13rem)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          {logoContent}
        </Box>
      )}

      {/* Single-product (hero) carts get the "Détails du panier" toggle, pinned
          to the right and vertically centered. It wraps to 2 lines if the label
          is long so it never collides with the centered logo, and being out of
          flow it can't change the header height between its open/closed labels. */}
      {isMobile && (cart?.lineItems?.length ?? 0) >= 1 && (
        <HStack
          as="button"
          type="button"
          onClick={onToggleHeroDetails}
          spacing={1}
          /* Always a filled pill so the control reads as a button in both
             states ("Panier" → open, "Fermer" → close). Single-line labels keep
             it compact next to the centered logo. */
          bg="rgba(0, 0, 0, 0.06)"
          color="var(--text, #1C1C1C)"
          borderRadius="full"
          px={3}
          py={1.5}
          transition="background-color 150ms ease, color 150ms ease"
          _active={{ opacity: 0.7 }}
          aria-expanded={heroDetailsOpen}
          aria-controls="mobile-cart-hero-details"
          position="absolute"
          top="50%"
          right={4}
          transform="translateY(-50%)"
          alignItems="center"
        >
          <Text fontSize="0.8125rem" fontWeight={500} lineHeight={1} textAlign="right" noOfLines={1} minW={0}>
            {heroDetailsOpen ? t('buttons.close') : t('commons.cart_short')}
          </Text>
          <HeaderChevron isOpen={heroDetailsOpen} />
        </HStack>
      )}
    </Box>
  );
};

function ArrowBackIcon({ onBack }: { onBack: () => void }) {
  return (
    <Box
      w="24px"
      h="24px"
      onClick={onBack}
      cursor="pointer"
      _hover={{ bg: 'none' }}
      data-ph-capture-attribute-tracking-id="checkout-form-back-button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={24}
        height={24}
        fill="none"
        role="img"
        aria-label="Close"
        style={{ color: 'var(--text, #000)' }}
      >
        <path
          fill="currentColor"
          fillOpacity={0.85}
          d="M3 12.402c0 .245.107.47.303.655l6.484 6.474c.195.186.4.274.635.274.478 0 .86-.352.86-.84a.855.855 0 0 0-.245-.615L8.85 16.123l-3.868-3.525-.205.478 3.145.195h12.353c.508 0 .86-.36.86-.869 0-.507-.352-.869-.86-.869H7.922l-3.145.196.205.488L8.85 8.682l2.187-2.227a.871.871 0 0 0 .244-.615c0-.488-.38-.84-.86-.84-.233 0-.439.078-.653.293l-6.465 6.455a.895.895 0 0 0-.303.654Z"
        />
      </svg>
    </Box>
  );
}
